import { Auth0Provider, type AppState } from '@auth0/auth0-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useLocation } from 'wouter'

interface Config {
  auth0_domain: string
  auth0_client_id: string
  auth0_audience: string
}

// Runtime (not build-time) config -- the Docker image is identical across
// environments, so Auth0 client id/domain/audience are fetched from Rails at
// boot rather than baked into the Vite bundle via import.meta.env. Mirrors
// Howie's Auth0Bootstrap (PD-27768), simplified: this app has only one login
// path, so there's no flag/legacy-fallback branch to gate on.
export function AppAuth0Provider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<Config | 'error' | null>(null)
  const [, navigate] = useLocation()

  useEffect(() => {
    fetch('/configs')
      .then((res) => res.json())
      .then(setConfig)
      .catch(() => setConfig('error'))
  }, [])

  if (config === null) {
    return (
      <div className="flex h-screen w-screen items-center justify-center text-ideon-gray-70">
        Loading…
      </div>
    )
  }

  if (config === 'error') {
    return (
      <div className="flex h-screen w-screen items-center justify-center text-ideon-state-error-dark">
        Failed to load configuration.
      </div>
    )
  }

  return (
    <Auth0Provider
      domain={config.auth0_domain}
      clientId={config.auth0_client_id}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: config.auth0_audience,
      }}
      onRedirectCallback={(appState?: AppState) => navigate(appState?.returnTo ?? '/')}
      useRefreshTokens
      cacheLocation="localstorage"
    >
      {children}
    </Auth0Provider>
  )
}
